import { SOUND_VARIANTS, type SoundType } from './soundMap';

export type HapticCue = SoundType | 'uiSave' | 'uiRollAgain';
export type HapticImpactStyle = 'LIGHT' | 'MEDIUM' | 'HEAVY';

interface HapticPulse {
  atMs: number;
  style: HapticImpactStyle;
}

export interface HapticPattern {
  pulses: HapticPulse[];
  // navigator.vibrate fallback pattern (vibrate, pause, vibrate, pause...)
  vibratePattern: number[];
}

const ROLL_PROFILE_TIGHT: HapticPattern = {
  // Static, pre-rendered timing profile: imitates short, dense dice rolling.
  pulses: [
    { atMs: 0, style: 'LIGHT' },
    { atMs: 48, style: 'LIGHT' },
    { atMs: 96, style: 'MEDIUM' },
    { atMs: 152, style: 'LIGHT' },
    { atMs: 214, style: 'MEDIUM' },
    { atMs: 286, style: 'LIGHT' },
    { atMs: 372, style: 'MEDIUM' },
    { atMs: 470, style: 'MEDIUM' },
    { atMs: 592, style: 'HEAVY' },
  ],
  vibratePattern: [11, 37, 11, 37, 14, 42, 11, 51, 14, 58, 11, 75, 14, 84, 14, 108, 18],
};

const ROLL_PROFILE_SWING: HapticPattern = {
  // Static, pre-rendered timing profile: imitates wider bounce between impacts.
  pulses: [
    { atMs: 0, style: 'LIGHT' },
    { atMs: 82, style: 'LIGHT' },
    { atMs: 164, style: 'MEDIUM' },
    { atMs: 252, style: 'LIGHT' },
    { atMs: 356, style: 'MEDIUM' },
    { atMs: 478, style: 'LIGHT' },
    { atMs: 628, style: 'HEAVY' },
  ],
  vibratePattern: [12, 70, 12, 70, 16, 72, 12, 92, 16, 106, 12, 138, 20],
};

const ROLL_PROFILE_DECAY: HapticPattern = {
  // Static, pre-rendered timing profile: stronger opening hits and slower decay.
  pulses: [
    { atMs: 0, style: 'MEDIUM' },
    { atMs: 62, style: 'HEAVY' },
    { atMs: 140, style: 'MEDIUM' },
    { atMs: 234, style: 'MEDIUM' },
    { atMs: 344, style: 'LIGHT' },
    { atMs: 478, style: 'LIGHT' },
    { atMs: 646, style: 'MEDIUM' },
  ],
  vibratePattern: [16, 46, 20, 58, 16, 78, 16, 94, 11, 123, 11, 157, 16],
};

const DICE_ROLL_PATTERN_BY_SOURCE: Record<string, HapticPattern> = Object.fromEntries(
  SOUND_VARIANTS.diceRoll.map((src, index) => {
    const profile = index % 3 === 0
      ? ROLL_PROFILE_TIGHT
      : index % 3 === 1
        ? ROLL_PROFILE_SWING
        : ROLL_PROFILE_DECAY;
    return [src, profile];
  }),
) as Record<string, HapticPattern>;

const SOUND_HAPTIC_DEFAULTS: Record<SoundType, HapticPattern> = {
  diceRoll: ROLL_PROFILE_TIGHT,
  fury: {
    pulses: [
      { atMs: 0, style: 'HEAVY' },
      { atMs: 132, style: 'MEDIUM' },
      { atMs: 268, style: 'HEAVY' },
      { atMs: 436, style: 'HEAVY' },
    ],
    vibratePattern: [45, 87, 56, 80, 70, 98, 78],
  },
  furyFirstRoll: {
    pulses: [
      { atMs: 0, style: 'HEAVY' },
      { atMs: 156, style: 'HEAVY' },
      { atMs: 344, style: 'HEAVY' },
    ],
    vibratePattern: [62, 94, 70, 118, 84],
  },
  rescueByFive: {
    pulses: [
      { atMs: 0, style: 'LIGHT' },
      { atMs: 74, style: 'MEDIUM' },
    ],
    vibratePattern: [15, 59, 20],
  },
  scoreSave: {
    pulses: [
      { atMs: 0, style: 'MEDIUM' },
      { atMs: 56, style: 'LIGHT' },
    ],
    vibratePattern: [16, 40, 10],
  },
  tunnelStop: {
    pulses: [
      { atMs: 0, style: 'MEDIUM' },
      { atMs: 118, style: 'MEDIUM' },
    ],
    vibratePattern: [26, 92, 26],
  },
  overThousand: {
    pulses: [
      { atMs: 0, style: 'HEAVY' },
      { atMs: 164, style: 'MEDIUM' },
      { atMs: 320, style: 'HEAVY' },
    ],
    vibratePattern: [40, 124, 32, 124, 46],
  },
  victory: {
    pulses: [
      { atMs: 0, style: 'LIGHT' },
      { atMs: 88, style: 'MEDIUM' },
      { atMs: 190, style: 'MEDIUM' },
      { atMs: 312, style: 'HEAVY' },
    ],
    vibratePattern: [16, 72, 20, 82, 20, 102, 32],
  },
  emoteSent: {
    pulses: [{ atMs: 0, style: 'LIGHT' }],
    vibratePattern: [10],
  },
  missedStraight: {
    pulses: [
      { atMs: 0, style: 'LIGHT' },
      { atMs: 78, style: 'LIGHT' },
      { atMs: 198, style: 'MEDIUM' },
    ],
    vibratePattern: [12, 66, 12, 108, 20],
  },
  // Strefy: premia to lekki, rosnący akcent; kara – krótkie, twarde szarpnięcie.
  zoneBonus: {
    pulses: [
      { atMs: 0, style: 'LIGHT' },
      { atMs: 90, style: 'MEDIUM' },
      { atMs: 196, style: 'MEDIUM' },
    ],
    vibratePattern: [14, 64, 20, 74, 26],
  },
  zonePenalty: {
    pulses: [
      { atMs: 0, style: 'HEAVY' },
      { atMs: 128, style: 'HEAVY' },
    ],
    vibratePattern: [40, 96, 48],
  },
};

const UI_HAPTIC_PATTERNS: Record<'uiSave' | 'uiRollAgain', HapticPattern> = {
  uiSave: {
    pulses: [
      { atMs: 0, style: 'MEDIUM' },
      { atMs: 62, style: 'LIGHT' },
    ],
    vibratePattern: [16, 46, 10],
  },
  uiRollAgain: {
    pulses: [
      { atMs: 0, style: 'LIGHT' },
      { atMs: 42, style: 'LIGHT' },
    ],
    vibratePattern: [10, 32, 10],
  },
};

export function resolveHapticPattern(cue: HapticCue, source?: string): HapticPattern {
  if (cue === 'uiSave' || cue === 'uiRollAgain') {
    return UI_HAPTIC_PATTERNS[cue];
  }
  if (cue === 'diceRoll' && source) {
    return DICE_ROLL_PATTERN_BY_SOURCE[source] || SOUND_HAPTIC_DEFAULTS.diceRoll;
  }
  return SOUND_HAPTIC_DEFAULTS[cue];
}
